import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useToast } from 'components/toast/ToastProvider';
import { useAuthStatus } from 'hooks/useAuthStatus';
import { useSubscriptionAccess } from 'hooks/useSubscriptionAccess';
import { PATHS } from 'routes/paths';
import { authStorage } from 'utils/storage';

const languages = [
  { name: 'English', code: 'en' },
  { name: 'Svenska', code: 'sv' },
  { name: 'Español', code: 'es' },
  { name: 'Deutsch', code: 'de' },
] as const;

type LanguageCode = (typeof languages)[number]['code'];

const getSavedLanguage = (): LanguageCode => {
  const savedLanguage = localStorage.getItem('siteLang');
  return languages.some((language) => language.code === savedLanguage)
    ? savedLanguage as LanguageCode
    : 'en';
};

const triggerGoogleTranslation = (code: LanguageCode, attempts = 0) => {
  const translateSelect = document.querySelector<HTMLSelectElement>('.goog-te-combo');

  if (translateSelect) {
    translateSelect.value = code === 'en' ? '' : code;
    translateSelect.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  if (attempts < 15) {
    window.setTimeout(() => triggerGoogleTranslation(code, attempts + 1), 200);
  }
};

const loadGoogleTranslate = (onReady: () => void) => {
  if (document.querySelector('.goog-te-combo')) {
    onReady();
    return;
  }

  const existingScript = document.querySelector<HTMLScriptElement>('script[data-google-translate]');
  if (existingScript) {
    if (existingScript.dataset.loaded === 'true') {
      onReady();
    } else {
      existingScript.addEventListener('load', onReady, { once: true });
    }
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  script.async = true;
  script.dataset.googleTranslate = 'true';
  script.addEventListener('load', () => {
    script.dataset.loaded = 'true';
    onReady();
  }, { once: true });
  document.head.appendChild(script);
};

const clearGoogleTranslateCookie = () => {
  const expiredCookie = 'googtrans=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
  document.cookie = expiredCookie;

  // Google Translate can create a domain cookie (for example .example.com),
  // which is separate from the host-only cookie created by this application.
  const hostParts = window.location.hostname.split('.');
  if (hostParts.length > 1) {
    const rootDomain = hostParts.slice(-2).join('.');
    document.cookie = `${expiredCookie};domain=${rootDomain}`;
    document.cookie = `${expiredCookie};domain=.${rootDomain}`;
  }
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [lang, setLang] = useState<LanguageCode>(getSavedLanguage);
  const isAuthenticated = useAuthStatus();
  const hasSubscriptionAccess = useSubscriptionAccess();
  const navigate = useNavigate();
  const { showSuccess } = useToast();

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: PATHS.home },
    { name: 'Library', path: PATHS.library },
    ...(hasSubscriptionAccess ? [] : [{ name: 'Pricing', path: PATHS.pricing }]),
    { name: 'Unsubscribe', path: PATHS.unsubscribe },
  ];

  const handleLogout = () => {
    authStorage.clearToken();
    setMobileOpen(false);
    showSuccess('Logged out successfully');
    navigate(PATHS.home);
  };

  const handleLanguageChange = (code: LanguageCode) => {
    setLang(code);
    localStorage.setItem('siteLang', code);
    if (code === 'en') {
      clearGoogleTranslateCookie();
    } else {
      document.cookie = `googtrans=/en/${code};path=/`;
    }

    setLanguageOpen(false);
    setMobileOpen(false);

    if (code === 'en') {
      // Google Translate mutates the current DOM. A reload after clearing its
      // cookie is the reliable way to restore the original English React tree.
      window.location.reload();
      return;
    }

    // The SDK is useful only after a visitor chooses another language. Loading
    // it on demand keeps Google Translate and its follow-up resources out of
    // the initial page's critical request chain.
    loadGoogleTranslate(() => triggerGoogleTranslation(code));
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[40] bg-[#2e3543]/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileOpen(false)}
      ></div>

      <div
        className={`z-50 w-full flex justify-center px-4 sm:px-8 pointer-events-none transition-all duration-500 top-6 ${
          scrolled ? 'fixed left-0 right-0 top-4' : 'sticky'
        }`}
      >
        <header
          className={`w-full max-w-7xl bg-[var(--bg-header)] backdrop-blur-xl rounded-2xl flex lg:grid lg:grid-cols-3 items-center justify-between px-5 py-3.5 pointer-events-auto transition-all relative border border-[var(--border-glass)] ${
            scrolled ? 'py-2 shadow-lg' : 'py-4'
          }`}
        >
          <div className="flex justify-start">
            <Link to={PATHS.home} onClick={() => setMobileOpen(false)}>
              <div className="flex items-center gap-2 pl-2 cursor-pointer group">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-[var(--primary)] group-hover:scale-110 transition-transform duration-500">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor" />
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="notranslate text-base font-extrabold text-[#2e3543] tracking-tight" translate="no">LearnSocialAI</span>
              </div>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center justify-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-[14px] font-bold text-slate-500 hover:text-[#2e3543] transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center justify-end gap-4 sm:gap-5">
            <div className="relative hidden lg:block">
              <button
                type="button"
                aria-label="Change language"
                aria-expanded={languageOpen}
                className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-xs font-black text-[#2e3543] transition-colors hover:bg-gray-100"
                onClick={() => setLanguageOpen((isOpen) => !isOpen)}
              >
                <svg aria-hidden="true" width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" />
                  <path strokeLinecap="round" d="M3 12h18M12 3c2.5 2.5 3.75 5.5 3.75 9S14.5 18.5 12 21c-2.5-2.5-3.75-5.5-3.75-9S9.5 5.5 12 3Z" />
                </svg>
                <span>{lang.toUpperCase()}</span>
              </button>

              {languageOpen && (
                <div className="absolute right-0 top-full z-50 mt-3 w-40 rounded-2xl border border-gray-100 bg-white p-2 shadow-xl">
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      type="button"
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-bold transition-colors ${
                        lang === language.code ? 'bg-gray-100 text-[#2e3543]' : 'text-slate-500 hover:bg-gray-50 hover:text-[#2e3543]'
                      }`}
                      onClick={() => handleLanguageChange(language.code)}
                    >
                      {language.name}
                      {lang === language.code && <span aria-hidden="true">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {isAuthenticated ? (
              <>
                <Link to={PATHS.account} className="hidden sm:flex lg:flex items-center justify-center w-10 h-10 rounded-full bg-[#2e3543] hover:bg-slate-800 transition-all shadow-sm">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5">
                    <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
                  </svg>
                </Link>
                <button
                  className="hidden lg:block bg-[#2e3543] text-white px-5 py-2.5 rounded-xl text-xs font-black tracking-widest hover:scale-105 transition-all shadow-sm"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </>
            ) : (
              <Link to={PATHS.signup} className="hidden lg:block bg-[#2e3543] text-white px-5 py-2.5 rounded-xl text-xs font-black tracking-widest hover:scale-105 transition-all shadow-sm">
                Sign up
              </Link>
            )}

            <button
              type="button"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 text-[#2e3543] active:scale-95 transition-transform"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <span className="sr-only">{mobileOpen ? 'Close menu' : 'Open menu'}</span>
              <svg aria-hidden="true" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
              </svg>
            </button>
          </div>

          <div
            className={`absolute top-full left-0 w-full mt-4 bg-white border border-gray-100 rounded-[2rem] shadow-2xl p-6 sm:p-8 flex flex-col transition-all duration-300 origin-top lg:hidden ${
              mobileOpen ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto' : 'opacity-0 -translate-y-4 scale-95 pointer-events-none'
            }`}
          >
            <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-4">
              <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Menu</span>
              <button
                type="button"
                aria-label="Close menu"
                className="p-2 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <svg aria-hidden="true" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#2e3543" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <nav className="flex flex-col gap-6 mb-10">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-lg font-bold text-[#2e3543] hover:translate-x-2 transition-transform"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <div className="mb-8 border-t border-gray-100 pt-6">
              <span className="mb-3 block text-xs font-black uppercase tracking-widest text-slate-400">Language</span>
              <div className="flex flex-wrap gap-2">
                {languages.map((language) => (
                  <button
                    key={language.code}
                    type="button"
                    className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                      lang === language.code ? 'bg-[#2e3543] text-white' : 'bg-gray-50 text-[#2e3543] hover:bg-gray-100'
                    }`}
                    onClick={() => handleLanguageChange(language.code)}
                  >
                    {language.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-auto space-y-3">
              {isAuthenticated ? (
                <>
                  <Link
                    to={PATHS.account}
                    className="flex items-center gap-4 p-4 mb-2 bg-gray-50 rounded-2xl border border-gray-100"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#2e3543] flex items-center justify-center">
                      <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-7 8-7s8 3 8 7" /></svg>
                    </div>
                    <span className="font-bold text-[#2e3543]">My Account</span>
                  </Link>
                  <button
                    type="button"
                    className="block w-full bg-white border-2 border-gray-100 text-[#2e3543] text-center py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:border-[#2e3543] transition-colors"
                    onClick={handleLogout}
                  >
                    Log out
                  </button>
                </>
              ) : (
                <Link
                  to={PATHS.signup}
                  className="block w-full bg-[#2e3543] text-white text-center py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg active:scale-95 transition-transform"
                  onClick={() => setMobileOpen(false)}
                >
                  Sign up
                </Link>
              )}
            </div>
          </div>
        </header>
      </div>
    </>
  );
};

export default Header;
